import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Block,
  BlockTitle,
  List,
  ListItem,
  Searchbar,
  Segmented,
  SegmentedButton,
  Sheet,
  Button,
  Fab,
  Toolbar,
} from 'konsta/react'
import {
  Plus,
  Users,
  Lock,
  Globe,
  Gamepad2,
  X,
  DollarSign,
  User,
  Percent,
  ArrowDownUp,
} from 'lucide-react'
import { formatCurrency, cn } from '@/lib/utils'

// --- Types ---

interface Room {
  id: string
  name: string
  blinds: string
  players: number
  maxPlayers: number
  type: 'NLH' | 'PLO'
  status: 'active' | 'waiting' | 'full'
  host: string
  rake: string
  minBuy: number
  maxBuy: number
  isPrivate?: boolean
}

// --- Mock Data ---

const mockRooms: Room[] = [
  { id: '1', name: '新手桌 #1', blinds: '$1/$2', players: 6, maxPlayers: 9, type: 'NLH', status: 'active', host: 'PWG 官方', rake: '5%', minBuy: 20000, maxBuy: 50000 },
  { id: '2', name: '新手桌 #2', blinds: '$1/$2', players: 3, maxPlayers: 9, type: 'NLH', status: 'waiting', host: 'PWG 官方', rake: '5%', minBuy: 20000, maxBuy: 50000 },
  { id: '3', name: '中級桌 #1', blinds: '$2/$5', players: 7, maxPlayers: 9, type: 'NLH', status: 'active', host: 'PWG 官方', rake: '5%', minBuy: 50000, maxBuy: 150000 },
  { id: '4', name: '中級桌 #2', blinds: '$2/$5', players: 9, maxPlayers: 9, type: 'NLH', status: 'full', host: 'PWG 官方', rake: '5%', minBuy: 50000, maxBuy: 150000 },
  { id: '5', name: '高額桌 #1', blinds: '$5/$10', players: 5, maxPlayers: 9, type: 'NLH', status: 'active', host: 'PWG 官方', rake: '5%', minBuy: 100000, maxBuy: 300000 },
  { id: '6', name: '高額桌 VIP', blinds: '$25/$50', players: 8, maxPlayers: 9, type: 'NLH', status: 'active', host: '王大明', rake: '5%+2.5%', minBuy: 500000, maxBuy: 1500000, isPrivate: true },
  { id: '7', name: 'David 的私人桌', blinds: '$5/$10', players: 4, maxPlayers: 6, type: 'NLH', status: 'waiting', host: 'David Chen', rake: '5%+2%', minBuy: 100000, maxBuy: 300000, isPrivate: true },
  { id: '8', name: '週末錦標賽', blinds: '$10/$20', players: 6, maxPlayers: 9, type: 'NLH', status: 'active', host: 'Sarah Lin', rake: '5%+3%', minBuy: 200000, maxBuy: 600000, isPrivate: true },
  { id: '9', name: 'PLO 新手桌', blinds: '$1/$2', players: 4, maxPlayers: 6, type: 'PLO', status: 'waiting', host: 'PWG 官方', rake: '5%', minBuy: 20000, maxBuy: 50000 },
  { id: '10', name: 'PLO 高額桌', blinds: '$5/$10', players: 5, maxPlayers: 6, type: 'PLO', status: 'active', host: 'PWG 官方', rake: '5%', minBuy: 100000, maxBuy: 300000 },
  { id: '11', name: '速戰桌 Turbo', blinds: '$2/$5', players: 6, maxPlayers: 6, type: 'NLH', status: 'full', host: 'PWG 官方', rake: '5%', minBuy: 50000, maxBuy: 150000 },
  { id: '12', name: '深籌桌 Deep', blinds: '$2/$5', players: 3, maxPlayers: 9, type: 'NLH', status: 'waiting', host: 'PWG 官方', rake: '5%', minBuy: 100000, maxBuy: 500000 },
]

// --- Tab definitions ---

type TabKey = 'all' | 'public' | 'private' | 'mine'

const tabs: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'public', label: '公開' },
  { key: 'private', label: '私人' },
  { key: 'mine', label: '我的' },
]

// --- Helpers ---

function getStatusConfig(status: Room['status']) {
  switch (status) {
    case 'active':
      return { label: '進行中', color: '#34C759', bgClass: 'bg-[#34C759]/15' }
    case 'waiting':
      return { label: '等待中', color: '#FF9500', bgClass: 'bg-[#FF9500]/15' }
    case 'full':
      return { label: '已滿', color: '#FF3B30', bgClass: 'bg-[#FF3B30]/15' }
  }
}

function StatusBadge({ status }: { status: Room['status'] }) {
  const config = getStatusConfig(status)
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium',
        config.bgClass,
      )}
      style={{ color: config.color }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full mr-1"
        style={{ backgroundColor: config.color }}
      />
      {config.label}
    </span>
  )
}

function RoomMediaIcon({ room }: { room: Room }) {
  return (
    <div
      className={cn(
        'w-10 h-10 rounded-xl flex items-center justify-center',
        room.isPrivate ? 'bg-[#FF9500]/15' : 'bg-[#007AFF]/15',
      )}
    >
      {room.isPrivate ? (
        <Lock className="w-5 h-5 text-[#FF9500]" />
      ) : (
        <Globe className="w-5 h-5 text-[#007AFF]" />
      )}
    </div>
  )
}

// --- Detail row helper ---

function DetailRow({
  icon: Icon,
  label,
  value,
  iconColor = '#8E8E93',
}: {
  icon: React.ElementType
  label: string
  value: string
  iconColor?: string
}) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex items-center gap-2.5 text-[#8E8E93]">
        <Icon className="w-4 h-4" style={{ color: iconColor }} />
        <span className="text-[14px]">{label}</span>
      </div>
      <span className="text-[14px] font-medium text-black">{value}</span>
    </div>
  )
}

// --- Player list for detail sheet ---

const mockPlayers = [
  '王大明', 'David Chen', 'Sarah Lin', '李小龍',
  '張三', '陳美玲', 'Mike W.', '林志穎', '周杰倫',
]

// --- Component ---

export function RoomsScreen() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  // Filter rooms
  const filteredRooms = useMemo(() => {
    let rooms = mockRooms

    // Filter by tab
    switch (activeTab) {
      case 'public':
        rooms = rooms.filter((r) => !r.isPrivate)
        break
      case 'private':
        rooms = rooms.filter((r) => r.isPrivate)
        break
      case 'mine':
        // Mock: show rooms hosted by the logged-in user
        rooms = rooms.filter((r) => r.host === '王大明')
        break
    }

    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      rooms = rooms.filter((r) =>
        r.name.toLowerCase().includes(q) ||
        r.host.toLowerCase().includes(q) ||
        r.type.toLowerCase().includes(q)
      )
    }

    return rooms
  }, [activeTab, searchQuery])

  const handleRoomTap = (room: Room) => {
    setSelectedRoom(room)
    setSheetOpen(true)
  }

  const handleCloseSheet = () => {
    setSheetOpen(false)
    setTimeout(() => setSelectedRoom(null), 300)
  }

  const handleJoinRoom = () => {
    if (selectedRoom) {
      handleCloseSheet()
      navigate(`/room/${selectedRoom.id}`)
    }
  }

  const roomPlayers = useMemo(() => {
    if (!selectedRoom) return []
    return mockPlayers.slice(0, selectedRoom.players)
  }, [selectedRoom])

  return (
    <>
      {/* Searchbar area */}
      <div className="px-4 pt-2 pb-1 bg-[#F2F2F7]">
        <Searchbar
          placeholder="搜尋房間名稱、主持人..."
          value={searchQuery}
          onInput={(e: any) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery('')}
        />
      </div>

      {/* Segmented tabs */}
      <div className="px-4 py-2 bg-[#F2F2F7]">
        <Segmented strong>
          {tabs.map((tab) => (
            <SegmentedButton
              key={tab.key}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </SegmentedButton>
          ))}
        </Segmented>
      </div>

      {/* Room count */}
      <BlockTitle className="!mt-1">
        {filteredRooms.length} 個房間
      </BlockTitle>

      {/* Room list */}
      {filteredRooms.length > 0 ? (
        <List strongIos insetIos>
          {filteredRooms.map((room) => {
            return (
              <ListItem
                key={room.id}
                link
                chevron={false}
                onClick={() => handleRoomTap(room)}
                media={<RoomMediaIcon room={room} />}
                title={
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{room.name}</span>
                    {room.isPrivate && (
                      <Lock className="w-3 h-3 text-[#FF9500]" />
                    )}
                  </div>
                }
                subtitle={
                  <span className="text-[#8E8E93]">
                    {room.blinds} &middot; {room.type}
                  </span>
                }
                after={
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={room.status} />
                    <span className="text-[12px] text-[#8E8E93]">
                      <Users className="w-3 h-3 inline mr-0.5 -mt-0.5" />
                      {room.players}/{room.maxPlayers}
                    </span>
                  </div>
                }
              />
            )
          })}
        </List>
      ) : (
        <Block className="text-center py-12">
          <Gamepad2 className="w-12 h-12 mx-auto text-[#C7C7CC] mb-3" />
          <p className="text-[#8E8E93] text-[15px]">
            {searchQuery ? '沒有找到符合的房間' : '目前沒有可用房間'}
          </p>
        </Block>
      )}

      {/* Spacer for FAB */}
      <div className="h-20" />

      {/* Floating "+" button */}
      <Fab
        className="fixed right-4 bottom-24 z-20"
        icon={<Plus className="w-6 h-6" />}
        onClick={() => navigate('/create-room')}
      />

      {/* Room Detail Sheet */}
      <Sheet
        opened={sheetOpen}
        onBackdropClick={handleCloseSheet}
        className="pb-safe"
      >
        {selectedRoom && (
          <div className="flex flex-col max-h-[85vh]">
            {/* Sheet header */}
            <Toolbar top>
              <div className="flex items-center justify-between w-full px-1">
                <span className="text-[17px] font-semibold">
                  房間詳情
                </span>
                <button
                  onClick={handleCloseSheet}
                  className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-[#8E8E93]" />
                </button>
              </div>
            </Toolbar>

            {/* Sheet content */}
            <div className="overflow-y-auto flex-1 px-4 pt-2 pb-4">
              {/* Room name and status */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <RoomMediaIcon room={selectedRoom} />
                  <div>
                    <h3 className="text-[17px] font-bold">{selectedRoom.name}</h3>
                    <p className="text-[13px] text-[#8E8E93]">
                      {selectedRoom.type} &middot; {selectedRoom.blinds}
                    </p>
                  </div>
                </div>
                <StatusBadge status={selectedRoom.status} />
              </div>

              {/* Details */}
              <div className="bg-[#F2F2F7] rounded-xl px-4 divide-y divide-black/5">
                <DetailRow
                  icon={User}
                  label="主持人"
                  value={selectedRoom.host}
                  iconColor="#007AFF"
                />
                <DetailRow
                  icon={DollarSign}
                  label="盲注"
                  value={selectedRoom.blinds}
                  iconColor="#34C759"
                />
                <DetailRow
                  icon={Percent}
                  label="抽水"
                  value={selectedRoom.rake}
                  iconColor="#FF9500"
                />
                <DetailRow
                  icon={ArrowDownUp}
                  label="最小/最大買入"
                  value={`${formatCurrency(selectedRoom.minBuy)} / ${formatCurrency(selectedRoom.maxBuy)}`}
                  iconColor="#AF52DE"
                />
                <DetailRow
                  icon={Users}
                  label="玩家人數"
                  value={`${selectedRoom.players} / ${selectedRoom.maxPlayers}`}
                  iconColor="#007AFF"
                />
                <DetailRow
                  icon={Gamepad2}
                  label="遊戲類型"
                  value={selectedRoom.type === 'NLH' ? 'No Limit Hold\'em' : 'Pot Limit Omaha'}
                  iconColor="#FF3B30"
                />
              </div>

              {/* Players in room */}
              <BlockTitle className="!px-0 !mt-4 !mb-2">
                在座玩家 ({selectedRoom.players})
              </BlockTitle>
              <div className="bg-[#F2F2F7] rounded-xl">
                <List strongIos className="!my-0">
                  {roomPlayers.map((name, i) => (
                    <ListItem
                      key={i}
                      title={name}
                      media={
                        <div className="w-8 h-8 rounded-full bg-[#007AFF]/15 flex items-center justify-center">
                          <span className="text-[13px] font-semibold text-[#007AFF]">
                            {name.charAt(0)}
                          </span>
                        </div>
                      }
                      after={
                        <span className="text-[12px] text-[#8E8E93]">
                          座位 {i + 1}
                        </span>
                      }
                    />
                  ))}
                </List>
              </div>
            </div>

            {/* Join button */}
            <div className="px-4 pt-2 pb-4">
              <Button
                large
                onClick={handleJoinRoom}
                disabled={selectedRoom.status === 'full'}
              >
                {selectedRoom.status === 'full' ? '房間已滿' : '加入'}
              </Button>
            </div>
          </div>
        )}
      </Sheet>
    </>
  )
}
